import { useCallback, useRef } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { isWtpFormatValid } from "@/utils/wtpUtils"

import { bffApi } from "../apiClient"
import queryKeys from "../queryKeys"

export interface LastUser {
  birthDate: string
  firstName: string
  lastName: string
  idImageData: string
  imageId: string
  isFullDetailsVisible: boolean
}

export interface ValidateWtpResponse {
  isWtpValid: boolean
  isWtpDuplicate: boolean
  isWtpOutDated: boolean
  lastUser?: LastUser
}
const validateWtpFn = async (
  wtpNumber: string,
  resortId: number,
  isUserProfileCard: boolean = false
) => {
  const response = await bffApi.post<ValidateWtpResponse>("/cards/validation", {
    wtpNumber,
    resortId,
    isUserProfileCard,
  })
  return response.data
}

export const useValidateWtpImperativeQuery = (resortId: number) => {
  const queryClient = useQueryClient()

  const validateWtpAsync = useCallback(
    async (
      wtpNumber: string,
      isUserProfileCard: boolean
    ): Promise<ValidateWtpResponse> => {
      const response = await queryClient.fetchQuery<ValidateWtpResponse>({
        queryKey: queryKeys.wtpValidation({
          resortId,
          wtpNumber,
          isUserProfileCard,
        }),
        queryFn: () => validateWtpFn(wtpNumber, resortId, isUserProfileCard),
        staleTime: 0,
      })
      return response
    },
    [queryClient, resortId]
  )

  return { validateWtpAsync }
}

const useValidateWtpQuery = (
  resortId: number,
  wtpNumber: string,
  isUserProfileCard: boolean,
  enabled: boolean = false
) => {
  const previousWtpNumber = useRef<string | undefined>(undefined)
  const previousIsUserProfileCard = useRef<boolean>(false)
  const queryClient = useQueryClient()

  const stableWtpNumber = isWtpFormatValid(wtpNumber) ? wtpNumber : undefined // stabilize wtpNumber to avoid unused queries

  const hasWtpNumberChanged = stableWtpNumber !== previousWtpNumber.current
  const hasIsUserProfileCardChanged =
    isUserProfileCard !== previousIsUserProfileCard.current
  if (hasWtpNumberChanged || hasIsUserProfileCardChanged) {
    queryClient.cancelQueries({
      queryKey: queryKeys.wtpValidation({
        resortId,
        wtpNumber: previousWtpNumber.current,
        isUserProfileCard: previousIsUserProfileCard.current,
      }),
    })
  }

  previousWtpNumber.current = stableWtpNumber
  previousIsUserProfileCard.current = isUserProfileCard

  return useQuery<ValidateWtpResponse>({
    queryKey: queryKeys.wtpValidation({
      resortId,
      wtpNumber: stableWtpNumber,
      isUserProfileCard,
    }),
    // safe to assume that stableWtpNumber is not undefined if enabled is true
    queryFn: () => validateWtpFn(stableWtpNumber!, resortId, isUserProfileCard),
    enabled: !!stableWtpNumber && enabled,
    staleTime: 0,
  })
}

export default useValidateWtpQuery
